import React, { useState } from "react";

import PaywallCTAModal from "components/account_management/PaywallCTAModal";
import { upgradeLink } from "components/account_management/utils";
import Button from "components/ui/button/Button";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { usePqlActioned } from "lib/hooks/api/users/usePqlActioned";
import { EventData } from "lib/tracking";
import { PqlActionType } from "lib/types/graphQLEnums";
import { FeatureType, getPaywallContent, PaidTag } from "./paywallUtils";

import css from "./PaywallContent.module.scss";

type Props = {
  featureType: FeatureType;
  trackingData?: EventData;
};

const BID_FEATURE_TYPES: FeatureType[] = [
  "BIDS",
  "CREATE_BIDS",
  "BID_RESPONSES",
  "BID_NO_BID_REPORTS",
  "BID_RESPONSE_EXPORTS",
  "BID_STOTLES_AI",
];

/**
 * This component is reused in the PaywallBanner and PaywallModal components. It has a description
 * for each paywall type, as well as the accompanying buttons which link to the upgrade pages.
 * @param param0
 * @returns
 */
export function PaywallContent({ featureType, trackingData }: Props): JSX.Element {
  const { title, description, bullets } = getPaywallContent(featureType);

  return (
    <div className={css.upgradeContainer}>
      <div className={css.upgradeContent}>
        <PaidTag />
        <h1 className={css.title}>{title}</h1>
        <p className={css.txt}>{description}</p>
        {bullets?.length && (
          <ul className={css.bullets}>
            {bullets?.map((content, index) => <li key={index}>{content}</li>)}
          </ul>
        )}
        <PaywallButtons featureType={featureType} trackingData={trackingData} />
      </div>
    </div>
  );
}

type PaywallButtonProps = {
  featureType?: FeatureType;
  trackingData?: EventData;
  primaryButtonText?: "Upgrade" | "Book a call";
  plansButtonText?: "Compare all plans" | "View pricing";
};

const PLANS_BUTTON_ACTION_TYPES: Record<
  NonNullable<PaywallButtonProps["plansButtonText"]>,
  PqlActionType
> = {
  "Compare all plans": PqlActionType.CompareAllPlans,
  "View pricing": PqlActionType.ViewPricing,
};

export function PaywallButtons({
  featureType,
  trackingData,
  plansButtonText = "Compare all plans",
  primaryButtonText = "Book a call",
}: PaywallButtonProps) {
  const [showDemoForm, setShowDemoForm] = useState<boolean | null>(null);

  const { mutate: actionPql } = usePqlActioned(undefined, trackingData);
  const user = useAssertCurrentUser();
  const isRelevancySearchNewUsersEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  const isAwardsFeatureType = featureType === "AWARDS" || featureType === "AWARDS_LOCKED";

  // Awards remains a self-serve upgrade (HubSpot payment link) unless the new-users flag is on,
  // in which case it joins every other paywall in routing to the in-app "Book a call" flow.
  const showSelfServeUpgrade = isAwardsFeatureType && !isRelevancySearchNewUsersEnabled;

  const primBtnText = showSelfServeUpgrade ? "Upgrade" : primaryButtonText;

  const onOpenChange = (open: boolean) => {
    if (!open) {
      setShowDemoForm(null);
    } else {
      console.warn("onOpenChange with open=true is unsupported in PaywallContent");
    }
  };

  const isBidFeatureType = featureType && BID_FEATURE_TYPES.includes(featureType);

  const UpgradeButton = () => {
    return (
      <Button
        as="a"
        buttonType="primary"
        target="_blank"
        href={upgradeLink(false, user.email)}
        onClick={() => {
          actionPql({
            actionType: PqlActionType.UpgradeToBasic,
            dataType: featureType ?? "",
            pageUrl: window.location.href,
          });
        }}
      >
        {primBtnText}
      </Button>
    );
  };

  const BookDemoButton = () => {
    return (
      <Button
        buttonType="primary"
        onClick={() => {
          actionPql({
            actionType: PqlActionType.BookADemoInApp,
            dataType: featureType ?? "",
            pageUrl: window.location.href,
          });
          setShowDemoForm(true);
        }}
      >
        {primBtnText}
      </Button>
    );
  };

  const renderPrimaryButton = () => {
    if (showSelfServeUpgrade) {
      return <UpgradeButton />;
    }
    return <BookDemoButton />;
  };

  return (
    <div className={css.btnContainer}>
      <PaywallCTAModal
        isOpen={showDemoForm !== null}
        onClose={() => onOpenChange(false)}
        showBookADemoForm={showDemoForm ?? false}
        featureType={featureType}
        defaultOpenTab={isBidFeatureType ? "bid" : "sales"}
      />
      {renderPrimaryButton()}

      <Button
        buttonType="secondary"
        onClick={() => {
          actionPql({
            actionType: PLANS_BUTTON_ACTION_TYPES[plansButtonText],
            dataType: featureType ?? "",
            pageUrl: window.location.href,
          });
          setShowDemoForm(false);
        }}
      >
        {plansButtonText}
      </Button>
    </div>
  );
}
